import {
  Badge,
  Banner,
  Button,
  FlexBox,
  Icon,
  Modal,
  ModalBody,
  ModalHead,
  Text,
} from '@dixa/lib-shared-habla';
import type {
  InsightInstrumentPrompt,
  InstrumentPanel,
} from 'lib-analytics-types';
import { ampli } from 'lib-shared-tracking';
import { useCallback, useState } from 'react';

import { useAnalyticsState } from 'analytics/components/panels/utils/useAnalyticsState/useAnalyticsState';
import { analyticsApi } from 'apis/analytics';
import { AiLoadingState } from 'components/aiLoadingState';

import { InstrumentPanelTitle } from '../panel/components/title/instrumentPanelTitle';

import { InstrumentInsightsInsight } from './components/insight/instrumentInsightsInsight';
import { InstrumentInsightsPrompts } from './components/prompts/instrumentInsightsPrompts';
import styles from './instrumentInsights.module.scss';

type InstrumentInsightsModalProps = {
  /** The instrument panel. */
  panel: InstrumentPanel;
  /** The prompts available to generate AI insights. */
  prompts: readonly InsightInstrumentPrompt[];
  /** Called when the modal is closed. */
  onClose: () => void;
};

export const InstrumentInsightsModal = ({
  panel,
  prompts,
  onClose,
}: InstrumentInsightsModalProps): JSX.Element => {
  const [prompt, setPrompt] = useState<InsightInstrumentPrompt | null>(null);
  const { dashboard, context, period, filters, settings } = useAnalyticsState();

  const { instruments, isFetching, isError, refetch, meta } =
    analyticsApi.useReadInstrumentsQuery(
      {
        id: panel.id,
        context,
        period,
        filters,
        settings,
        meta: {
          ai_prompt_id: prompt?.id,
        },
      },
      {
        skip: !prompt,
        selectFromResult: ({ data, ...rest }) => ({
          ...rest,
          instruments: data?.data.instruments ?? [],
          meta: data?.meta,
        }),
      },
    );

  const handleSelectPrompt = useCallback(
    (prompt: InsightInstrumentPrompt) => {
      setPrompt(prompt);

      ampli.analyticsInsightsPromptClicked({
        dashboard: dashboard.title,
        panel: panel.title,
        prompt: prompt.title,
      });
    },
    [dashboard.title, panel.title],
  );

  return (
    <Modal isOpen className={styles.modal} onClose={onClose}>
      <ModalHead
        title={
          <FlexBox gap="200" data-analytics-panel={panel.id}>
            <Badge variant="primary" size="medium">
              <FlexBox gap="200" alignItems="center">
                <Icon name="ai" size={16} />
                AI-powered insights
              </FlexBox>
            </Badge>

            <InstrumentPanelTitle panel={panel} />
          </FlexBox>
        }
        onClose={onClose}
      />

      <ModalBody>
        <FlexBox flexDirection="column" gap="400">
          <Text.Paragraph variant="2" subdued>
            Leveraging AI technology, this feature dives deep into customer
            ratings and comments, equipping you with invaluable insights to
            refine practices, elevate customer satisfaction, and pinpoint
            opportunities for growth and exceptional service delivery.
          </Text.Paragraph>

          {prompt ? (
            <div>
              <Button
                variant="secondary"
                icon="arrow-left"
                onClick={() => setPrompt(null)}
              >
                Back to overview
              </Button>
            </div>
          ) : (
            <Text.Paragraph variant="1" subdued>
              Choose an insight
            </Text.Paragraph>
          )}

          {isError && (
            <Banner
              variant="fail"
              headline="An error occurred generating AI insights"
              description={
                <Text.Paragraph>
                  Our team has been notified and is looking into the issue.
                </Text.Paragraph>
              }
            />
          )}

          <div className={styles.content}>
            {isFetching && (
              <FlexBox
                className={styles.loading}
                flexDirection="column"
                alignItems="center"
                justifyContent="center"
                gap="400"
              >
                <AiLoadingState label="Generating insights" />
              </FlexBox>
            )}

            {prompt && instruments.length > 0 ? (
              <InstrumentInsightsInsight
                instruments={instruments}
                panel={panel}
                prompt={prompt}
                requestId={meta?.request_id}
                onRefetch={refetch}
              />
            ) : (
              <>
                <InstrumentInsightsPrompts
                  prompts={prompts}
                  onSelectPrompt={handleSelectPrompt}
                />
              </>
            )}
          </div>
        </FlexBox>
      </ModalBody>
    </Modal>
  );
};
